import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { API_ROUTES } from '@mangelapp/shared';

export interface ClientType {
  id: string;
  name: string;
  displayOrder: number;
  createdAt: string;
}

export function useClientTypes() {
  return useQuery<ClientType[]>({
    queryKey: ['client-types'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ success: boolean; data: ClientType[] }>(
          API_ROUTES.CLIENT_TYPES.LIST
        );
        console.log('🔍 useClientTypes - API response:', response);
        if (!response.success || !response.data) {
          console.error('❌ useClientTypes - Invalid response:', response);
          throw new Error('Failed to fetch client types');
        }
        console.log('✅ useClientTypes - Fetched client types:', response.data.length);
        return response.data;
      } catch (error) {
        console.error('❌ useClientTypes - Error:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour - client types are global and rarely change
  });
}

/**
 * Hook to get client type display information (name, localized name)
 */
export function useClientTypeDisplay(clientTypeId: string | null | undefined) {
  const { data: clientTypes = [] } = useClientTypes();
  const { t } = useTranslation(['pages.projects']);
  
  const clientType = clientTypeId ? clientTypes.find(ct => ct.id === clientTypeId) : null;
  
  const getDisplayName = (): string => {
    if (!clientType) return '-';
    
    // Map database values to translation keys
    const clientTypeMap: Record<string, string> = {
      'Bay': t('pages.projects:clientType.bay'),
      'Bayan': t('pages.projects:clientType.bayan'),
      'Firma': t('pages.projects:clientType.firma'),
    };
    
    if (clientTypeMap[clientType.name]) {
      return clientTypeMap[clientType.name];
    }
    
    // Fallback to database name if no translation found
    return clientType.name;
  };
  
  return {
    clientType,
    displayName: getDisplayName(),
    isLoading: !clientTypes.length && !!clientTypeId,
  };
}
